// src/pages/Subscriptions/SubscriptionsPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptions();
    }
  }, [isAuthenticated]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const data = await api.get('/subscriptions/').catch(() => ({ results: [] }));
      setSubscriptions(data.results || []);
    } catch (err) {
      setError(err.data?.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      trial: 'info',
      pending: 'warning',
      expired: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (window.confirm('Are you sure you want to cancel this subscription?')) {
      try {
        await api.post(`/subscriptions/${subscriptionId}/cancel/`);
        fetchSubscriptions(); // Refresh the list
      } catch (err) {
        alert('Failed to cancel subscription');
      }
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        My Subscriptions
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your active subscriptions and billing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {subscriptions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Active Subscriptions
          </Typography>
          <Typography color="text.secondary" paragraph>
            You haven't subscribed to any solutions yet.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/solutions')}
          >
            Browse Solutions
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {subscriptions.map((sub) => (
            <Grid item xs={12} key={sub.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{sub.solution_title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sub.organization_name}
                      </Typography>
                    </Box>
                    <Chip
                      label={sub.status}
                      color={getStatusColor(sub.status)}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Plan
                      </Typography>
                      <Typography variant="body1">{sub.pricing_plan_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Started
                      </Typography>
                      <Typography variant="body1">
                        {new Date(sub.start_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    {sub.end_date && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Ends
                        </Typography>
                        <Typography variant="body1">
                          {new Date(sub.end_date).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Auto-renew
                      </Typography>
                      <Typography variant="body1">
                        {sub.auto_renew ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {sub.is_trial_status && sub.trial_end_date && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Trial ends on {new Date(sub.trial_end_date).toLocaleDateString()}
                    </Alert>
                  )}

                  {sub.feature_usage && sub.feature_usage.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Usage
                      </Typography>
                      <Grid container spacing={2}>
                        {sub.feature_usage.map((feature) => (
                          <Grid item xs={12} sm={6} md={4} key={feature.feature_name}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{feature.feature_name}:</Typography>
                              <Typography variant="body2">
                                {feature.usage_count} / {feature.usage_limit}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary">
                    View Details
                  </Button>
                  <Button 
                    size="small" 
                    color="error"
                    onClick={() => handleCancelSubscription(sub.id)}
                  >
                    Cancel Subscription
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default SubscriptionsPage;